import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, Leaf, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo , EstadoBadge } from "../components/Semaforo";
import { COMPONENTES_AMBIENTALES, COMMITMENT_ESTADOS } from "../lib/constants";
import { formatDate, formatLabel } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";

export default function RCA() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  function empty() {
    return {
      codigo: "", nombre: "", project_id: "", description: "",
      componente: "", frecuencia: "",
      fecha_inicio: "", fecha_comprometida: "",
      responsable: "", responsable_email: "",
      evidencia_requerida: "", estado: "pendiente",
    };
  }

  const load = async () => { const { data } = await api.get("/rca"); setItems(data); };
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const submit = async () => {
    try {
      const p = { ...form };
      p.fecha_comprometida = p.fecha_comprometida || null;
      p.fecha_inicio = p.fecha_inicio || null;
      if (!p.project_id) p.project_id = null;
      await api.post("/rca", p);
      toast.success("RCA creada");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (id) => { if (!(await confirm({ title: "Eliminar RCA", description: "¿Deseas eliminar esta RCA? Esta acción no se puede deshacer.", danger: true }))) return; await api.delete(`/rca/${id}`); load(); };

  const notifyNow = async (r) => {
    if (!r.responsable_email) {
      toast.error("Esta RCA no tiene email de responsable. Edita la RCA para agregarlo.");
      return;
    }
    try {
      const { data } = await api.post(`/rca/${r.id}/notify`);
      toast.success(`Recordatorio enviado a ${data.to} (${data.days_left} días)`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : "No se pudo enviar el recordatorio";
      toast.error(msg);
    }
  };

  const daysLeft = (d) => {
    if (!d) return null;
    const t = new Date(); t.setHours(0,0,0,0);
    return Math.ceil((new Date(d).getTime() - t.getTime()) / 86400000);
  };

  return (
    <div data-testid="rca-page">
      <PageHeader title="Compromisos RCA" description="Compromisos derivados de Resoluciones de Calificación Ambiental, tanto sociales como ambientales (agua, aire, suelo, biodiversidad, comunidades, etc.)."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-slate-900 text-white" data-testid="new-rca-btn"><Plus weight="bold" /> Nueva RCA</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>Nueva RCA</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Código</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="RCA-2024-001" data-testid="rca-codigo-input" /></div>
                <div><Label>Estado</Label>
                  <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{COMMITMENT_ESTADOS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
                <div>
                  <Label>Componente</Label>
                  <Select value={form.componente || "__none__"} onValueChange={(v) => setForm({ ...form, componente: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {COMPONENTES_AMBIENTALES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Frecuencia</Label><Input value={form.frecuencia} onChange={(e) => setForm({ ...form, frecuencia: e.target.value })} placeholder="Mensual / Trimestral / Anual" /></div>
                <div><Label>Fecha inicio</Label><Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
                <div><Label>Fecha comprometida (término)</Label><Input type="date" value={form.fecha_comprometida} onChange={(e) => setForm({ ...form, fecha_comprometida: e.target.value })} /></div>
                <div><Label>Responsable</Label><Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} /></div>
                <div className="col-span-2"><Label>Email(s) responsable — separa con coma para varios</Label><Input type="text" value={form.responsable_email} onChange={(e) => setForm({ ...form, responsable_email: e.target.value })} placeholder="nombre@empresa.cl, otro@empresa.cl" /></div>
                <div>
                  <Label>Proyecto</Label>
                  <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Evidencia requerida</Label><Input value={form.evidencia_requerida} onChange={(e) => setForm({ ...form, evidencia_requerida: e.target.value })} /></div>
                <div className="col-span-2"><Label>Descripción</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-rca-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-6 py-6">
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Código</TableHead><TableHead>Nombre</TableHead><TableHead>Componente</TableHead>
              <TableHead>Frecuencia</TableHead><TableHead>Fecha término</TableHead><TableHead>Faltan</TableHead>
              <TableHead>Responsable</TableHead>
              <TableHead>Estado</TableHead><TableHead>Semáforo</TableHead><TableHead className="text-right">—</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-500">Sin RCA registradas.</TableCell></TableRow>}
              {items.map(r => {
                const dl = daysLeft(r.fecha_comprometida);
                const dlLabel = dl === null ? "—"
                  : dl < 0 ? `Atrasado ${Math.abs(dl)}d`
                  : dl === 0 ? "Hoy"
                  : `${dl}d`;
                const dlCls = dl === null ? "text-slate-400"
                  : dl < 0 || dl <= 3 ? "text-red-600 font-medium"
                  : dl <= 30 ? "text-amber-600" : "text-slate-700";
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.codigo}</TableCell>
                    <TableCell className="font-medium flex items-center gap-1"><Leaf size={14} className="text-brand-green-600" />{r.nombre}</TableCell>
                    <TableCell className="capitalize">{r.componente || "—"}</TableCell>
                    <TableCell>{r.frecuencia || "—"}</TableCell>
                    <TableCell>{formatDate(r.fecha_comprometida)}</TableCell>
                    <TableCell className={dlCls}>{dlLabel}</TableCell>
                    <TableCell>
                      <div>{r.responsable || "—"}</div>
                      {r.responsable_email && <div className="text-[10px] text-slate-400">{r.responsable_email}</div>}
                    </TableCell>
                    <TableCell className="capitalize"><EstadoBadge value={r.estado} /></TableCell>
                    <TableCell><Semaforo value={r.semaforo} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" title="Enviar recordatorio ahora" onClick={() => notifyNow(r)} data-testid={`rca-notify-${r.id}`}>
                        <PaperPlaneTilt size={14} className="text-brand-green-700" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash size={14} className="text-red-600" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
